import { type CertificateValues, certificateSchema, DayInput, toCertificateInput } from '@felix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiError, type CertificateRecord, type CertificateType, officeApi } from '../api.js';
import { ErrorAlert } from './ErrorAlert.js';

interface Props {
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
  person: { userId: string; fullName: string };
  types: readonly CertificateType[];
  /** Düzenlenen kayıt; yoksa yeni belge. */
  record: CertificateRecord | null;
  /** Yeni belgede önceden seçili tür (ör. eksik zorunlu belge satırından). */
  presetTypeId?: string | null;
}

const empty = (typeId = ''): CertificateValues => ({
  certificateTypeId: typeId,
  number: '',
  issuedOn: '',
  expiresOn: '',
  issuedBy: '',
});

/**
 * Belge ekle / düzenle (ADR 0016 §8): belgeleri ofis yönetir. Düzeltme satır sürümüyle gider;
 * araya başka değişiklik girdiyse sunucu `ROW_VERSION_CONFLICT` döner.
 */
export function CertificateModal({
  opened,
  onClose,
  onSaved,
  person,
  types,
  record,
  presetTypeId,
}: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<CertificateValues>({
    resolver: zodResolver(certificateSchema),
    defaultValues: empty(),
  });
  const { errors } = form.formState;

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(
      record
        ? {
            certificateTypeId: record.typeId,
            number: record.number ?? '',
            issuedOn: record.issuedOn ?? '',
            expiresOn: record.expiresOn ?? '',
            issuedBy: record.issuedBy ?? '',
          }
        : empty(presetTypeId ?? ''),
    );
  }, [opened, record, presetTypeId, form]);

  const options = useMemo(
    () =>
      types.map((type) => ({
        value: type.id,
        label: `${type.name} (${type.code})`,
      })),
    [types],
  );

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const input = toCertificateInput(values);
      if (record) await officeApi.updateCertificate(record.id, record.rowVersion, input);
      else await officeApi.addCertificate(person.userId, input);
      onSaved();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
    }
  });

  const message = (key: string | undefined) => (key ? t(key) : undefined);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(record ? 'certificates.form.editTitle' : 'certificates.form.addTitle')}
      centered
      size="lg"
      radius="md"
      closeButtonProps={{ 'aria-label': t('fleet.closeDialog') }}
    >
      <Text fz="sm" c="dimmed" mb="lg">
        {t('certificates.form.person', { name: person.fullName })}
      </Text>
      <ErrorAlert code={error} />
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <Controller
            control={form.control}
            name="certificateTypeId"
            render={({ field }) => (
              <Select
                label={t('certificates.form.type')}
                placeholder={t('certificates.form.typePlaceholder')}
                data={options}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                data-autofocus
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                onBlur={field.onBlur}
                error={message(errors.certificateTypeId?.message)}
              />
            )}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('certificates.form.number')}
              error={message(errors.number?.message)}
              {...form.register('number')}
            />
            <TextInput
              label={t('certificates.form.issuedBy')}
              error={message(errors.issuedBy?.message)}
              {...form.register('issuedBy')}
            />
            {/* Gün `GG.AA.YYYY`, işletim sisteminin dil biçiminden bağımsız (M6f G2-29; ADR 0033) */}
            <Controller
              control={form.control}
              name="issuedOn"
              render={({ field }) => (
                <DayInput
                  label={t('certificates.form.issuedOn')}
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                  clearable
                  error={message(errors.issuedOn?.message)}
                />
              )}
            />
            <Controller
              control={form.control}
              name="expiresOn"
              render={({ field }) => (
                <DayInput
                  label={t('certificates.form.expiresOn')}
                  description={t('certificates.form.expiresHint')}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                  clearable
                  error={message(errors.expiresOn?.message)}
                />
              )}
            />
          </SimpleGrid>
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('certificates.form.cancel')}
            </Button>
            <Button
              type="submit"
              loading={form.formState.isSubmitting}
              leftSection={<IconDeviceFloppy size={18} />}
            >
              {t('certificates.form.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
